/**
 * 将向导中的库表选择与字段列配置组装为后端 `cube_config` 结构。
 * 口径：仅 `fieldsValue` 勾选的列进入 dimensions / measures，顺序与 `fieldColumns` 一致。
 */
import type {
  ICubeConfigPayload,
  ICubeDimensionConfig,
  ICubeMeasureConfig,
  IFieldConfigOption,
  IStepFieldConfigState,
  IStepSelectTablesState,
  IYamlFieldColumn,
} from '../types';
import { parseJsonArrayString } from './fieldConfigRowUtils';

/** 组装 cube_config 时所需的 Step1（选库选表）+ Step2（fieldColumns）快照 */
export interface IBuildCubeConfigInput {
  selectTables: IStepSelectTablesState;
  fieldConfig: IStepFieldConfigState;
  /** 优先于表展示名，用于 generate 请求体 `cube_config.name` */
  cubeName?: string;
}

/** 用 Step1 下拉选项解析主表展示名与 `table_desc`；缺失时表名回退为 table_id */
const resolvePrimaryTable = (
  selectTables: IStepSelectTablesState,
  tableId: string,
): { tableName: string; tableDesc: string; } => {
  const opt = (selectTables.tablesOptions as IFieldConfigOption[])?.find(
    (o) => o.value === tableId,
  );
  const tableName =
    selectTables.tablesName ||
    // eslint-disable-next-line no-nested-ternary
    (typeof opt?.label === 'string' && opt.label
      ? opt.label
      : typeof opt?.text === 'string' && opt.text
        ? String(opt.text)
        : tableId);
  const tableDesc = opt?.table_desc ?? '';
  return { tableName, tableDesc };
};

/**
 * 维度 `meta.sample_values`：接口数组原样；
 * 字符串为 JSON 数组时解析（如 `["1","2"]`），否则非空则作为单元素数组。
 */
const dimensionSampleValues = (col: IYamlFieldColumn): unknown[] => {
  if (Array.isArray(col.default_sample_values)) {
    return col.default_sample_values;
  }
  if (typeof col.default_sample_values === 'string') {
    const s = col.default_sample_values.trim();
    if (!s) {
      return [];
    }
    const parsed = parseJsonArrayString(s);
    if (parsed) {
      return parsed;
    }
    return [s];
  }
  return [];
};

/**
 * 指标 `filters`：数组（接口/历史）原样；字符串（表格编辑）非空则单元素数组，不做 JSON 解析。
 */
const measureFilters = (col: IYamlFieldColumn): unknown[] => {
  if (Array.isArray(col.filters)) {
    return col.filters;
  }
  if (typeof col.filters_text === 'string') {
    const s = col.filters_text;
    return s ? [s] : [];
  }
  return [];
};

/**
 * 将 Step1 + Step2 状态组装为 `generate_yaml_from_config` 请求体中的 `cube_config`。
 *
 * @param state - `selectTables` 提供 schema、主表 id、库 UUID、`sql_table` 用表名；
 *   `fieldConfig.fieldColumns` + `fieldsValue` 仅输出用户勾选参与 Cube 的列。
 * @returns 符合接口约定的 `ICubeConfigPayload`（含顶层表信息、dimensions、measures）。
 */
export function buildCubeConfig(state: IBuildCubeConfigInput): ICubeConfigPayload {
  const { selectTables, fieldConfig, cubeName } = state;
  const { fieldColumns, fieldsValue } = fieldConfig;
  const selected = new Set(fieldsValue);
  const columnsForCube = fieldColumns.filter((col) =>
    selected.has(col.source_column),
  );
  const {
    databaseValue,
    dataBaseData,
    schemaValue,
    tablesValue,
  } = selectTables;
  const tableId = tablesValue[0] || '';
  const { tableName, tableDesc } = resolvePrimaryTable(selectTables, tableId);
  // 完整表名：schema.物理表名（与 table_id 区分）
  const sqlTable =
    schemaValue && tableName
      ? `${schemaValue}.${tableName}`
      : tableName;

  const dbItem = dataBaseData?.find((d) => d.database_uuid === databaseValue);
  const databaseName = dbItem?.database_name;

  const dimensions: ICubeDimensionConfig[] = [];
  const measures: ICubeMeasureConfig[] = [];

  for (const col of columnsForCube) {
    const cubeName = col.default_name || col.source_column;
    const displayName = col.default_display_name || cubeName;
    const isCustom = Boolean(col.is_custom);
    const nameZh =
      col.default_name_zh || col.default_display_name || col.source_column;
    const nameEn =
      col.default_name_en || col.default_display_name || col.source_column;
    const description = col.default_description || '';

    if (col.default_kind === 'dimension') {
      const dimType = col.default_type || 'string';
      // 未填 SQL 时由主表列生成占位表达式，供后端/LLM 解析
      const sql = col.default_sql?.trim()
        ? col.default_sql
        : `{CUBE}.${col.source_column}`;
      dimensions.push({
        source_column: col.source_column,
        name: cubeName,
        display_name: displayName,
        type: dimType,
        sql,
        description,
        is_custom: isCustom,
        meta: {
          name_zh: nameZh,
          name_en: nameEn,
          sample_values: dimensionSampleValues(col),
          dim_summary_value: col.default_dim_summary_value,
          dim_table: col.default_dim_table,
          dim_table_column: col.default_dim_table_column,
        },
      });
    } else {
      measures.push({
        source_column: col.source_column,
        name: cubeName,
        display_name: displayName,
        type: col.default_type || 'sum',
        sql: col.default_sql || '',
        description,
        format: col.default_format || '',
        granularity: col.default_granularity || '',
        filters: measureFilters(col),
        common_query: col.default_common_query || '',
        is_custom: isCustom,
        meta: {
          name_zh: nameZh,
          name_en: nameEn,
        },
      });
    }
  }

  // cube_name 与当前主表一致时优先；Step1 改表后由 handleStepOneNext 同步 tablesName
  const resolvedCubeName =
    cubeName?.trim() || tableName || '';

  return {
    name: resolvedCubeName,
    table_id: tableId,
    table_name: tableName,
    database_name: databaseName,
    // 优先使用接口返回的表描述；无描述时回退为表名占位
    description: tableDesc || "",
    data_source: databaseValue,
    sql_table: sqlTable,
    meta: {
      label: [],
    },
    dimensions,
    measures,
  };
}
